import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { fetchTokenBundle, fetchTokenNarrativeFlow } from './tokenIntelligenceApi';

// Module-level in-flight deduplication maps. Concurrent calls with the same key
// (e.g. React 18 StrictMode double-mount) share the same Promise and fire one request.
const inflightBundle = new Map();
const inflightNflow = new Map();

function createEmptyData() {
  return {
    metadata: null,
    priceHistory: { points: [] },
    liquidity: { latest: null, points: [] },
    stories: { stories: [] },
    theses: { theses: [] },
    narrativeFlow: null,
  };
}

function createEmptyLoading() {
  return {
    metadata: false,
    priceHistory: false,
    liquidity: false,
    stories: false,
    theses: false,
    narrativeFlow: false,
  };
}
 
function createEmptyErrors() {
  return {
    metadata: '',
    priceHistory: '',
    liquidity: '',
    stories: '',
    theses: '',
    narrativeFlow: '',
  };
}

function isValidAddress(value) {
  return /^0x[0-9a-fA-F]{40}$/.test(String(value || '').trim());
}

export default function useTokenIntelligenceData(address, options = {}) {
  const normalizedAddress = useMemo(() => String(address || '').trim().toLowerCase(), [address]);
  const range = String(options.range || '24h').trim().toLowerCase();
  const enabled = options.enabled !== false;

  const requestIdRef = useRef(0);
  const mountedRef = useRef(true);
  const bundleCacheRef = useRef(new Map());
  const nflowCacheRef = useRef(new Map());

  const [data, setData] = useState(() => createEmptyData());
  const [loading, setLoading] = useState(() => createEmptyLoading());
  const [errors, setErrors] = useState(() => createEmptyErrors());
  const [lastUpdated, setLastUpdated] = useState(null);

  // Load fast bundle (metadata, priceHistory, liquidity, stories, theses) via single request
  const loadBundle = useCallback(async ({ force = false } = {}) => {
    const requestId = requestIdRef.current + 1;
    requestIdRef.current = requestId;

    if (!enabled || !isValidAddress(normalizedAddress)) {
      if (!mountedRef.current) return;
      setData(createEmptyData());
      setLoading(createEmptyLoading());
      setErrors({
        ...createEmptyErrors(),
        metadata: normalizedAddress ? 'Invalid token address' : '',
      });
      setLastUpdated(null);
      return;
    }

    const bundleKey = `${normalizedAddress}|${range}`;
    const bundleCached = !force ? bundleCacheRef.current.get(bundleKey) : null;

    setLoading({
      metadata: !bundleCached,
      priceHistory: !bundleCached,
      liquidity: !bundleCached,
      stories: !bundleCached,
      theses: !bundleCached,
      narrativeFlow: true,
    });
    setErrors(createEmptyErrors());

    let bundle = bundleCached;
    if (!bundle) {
      // Deduplicate concurrent in-flight requests for the same bundle key
      let promise = inflightBundle.get(bundleKey);
      if (!promise) {
        promise = fetchTokenBundle(normalizedAddress, { range });
        inflightBundle.set(bundleKey, promise);
        promise.finally(() => inflightBundle.delete(bundleKey));
      }
      try {
        bundle = await promise;
        if (bundle) bundleCacheRef.current.set(bundleKey, bundle);
      } catch (err) {
        if (!mountedRef.current || requestId !== requestIdRef.current) return;
        const msg = err && err.message ? err.message : 'Failed to load token data';
        setData(createEmptyData());
        setErrors({ ...createEmptyErrors(), metadata: msg, priceHistory: msg, liquidity: msg, stories: msg, theses: msg });
        setLoading(createEmptyLoading());
        return;
      }
    }

    if (!mountedRef.current || requestId !== requestIdRef.current) return;

    const priceHistoryRaw = bundle && bundle.priceHistory;
    const priceHistory = priceHistoryRaw && typeof priceHistoryRaw === 'object'
      ? { ...priceHistoryRaw, points: Array.isArray(priceHistoryRaw.points) ? priceHistoryRaw.points : [] }
      : { points: [] };

    const liquidityRaw = bundle && bundle.liquidity;
    const liquidity = liquidityRaw && typeof liquidityRaw === 'object'
      ? {
        ...liquidityRaw,
        latest: liquidityRaw.latest && typeof liquidityRaw.latest === 'object' ? liquidityRaw.latest : null,
        points: Array.isArray(liquidityRaw.points) ? liquidityRaw.points : [],
      }
      : { latest: null, points: [] };

    setData((prev) => ({
      ...prev,
      metadata: (bundle && bundle.metadata) || null,
      priceHistory,
      liquidity,
      stories: { stories: Array.isArray(bundle && bundle.stories) ? bundle.stories : [] },
      theses: { theses: Array.isArray(bundle && bundle.theses) ? bundle.theses : [] },
    }));
    setLoading((prev) => ({ ...prev, metadata: false, priceHistory: false, liquidity: false, stories: false, theses: false }));
    setLastUpdated(new Date().toISOString());
  }, [enabled, normalizedAddress, range]);

  // Separately load the expensive narrative-flow after the bundle has rendered
  const loadNarrativeFlow = useCallback(async ({ force = false } = {}) => {
    if (!enabled || !isValidAddress(normalizedAddress)) return;

    const nflowKey = `${normalizedAddress}|24h|5000`;
    const nflowCached = !force ? nflowCacheRef.current.get(nflowKey) : null;

    if (nflowCached) {
      setData((prev) => ({ ...prev, narrativeFlow: nflowCached }));
      setLoading((prev) => ({ ...prev, narrativeFlow: false }));
      return;
    }

    // Deduplicate concurrent in-flight requests
    let promise = inflightNflow.get(nflowKey);
    if (!promise) {
      promise = fetchTokenNarrativeFlow(normalizedAddress, { range: '24h', minUsd: 5000 });
      inflightNflow.set(nflowKey, promise);
      promise.finally(() => inflightNflow.delete(nflowKey));
    }

    try {
      const nflow = await promise;
      if (!mountedRef.current) return;
      const normalized = nflow && typeof nflow === 'object' ? nflow : null;
      if (normalized) nflowCacheRef.current.set(nflowKey, normalized);
      setData((prev) => ({ ...prev, narrativeFlow: normalized }));
      setErrors((prev) => ({ ...prev, narrativeFlow: '' }));
    } catch (err) {
      if (!mountedRef.current) return;
      setErrors((prev) => ({ ...prev, narrativeFlow: err && err.message ? err.message : 'Failed to load narrative flow' }));
    } finally {
      if (mountedRef.current) setLoading((prev) => ({ ...prev, narrativeFlow: false }));
    }
  }, [enabled, normalizedAddress]);

  // Mount/unmount tracking
  useEffect(() => {
    mountedRef.current = true;
    return () => { mountedRef.current = false; };
  }, []);

  // Load bundle on address/range change
  useEffect(() => {
    const run = async () => { try { await loadBundle(); } catch (e) {} };
    run();
  }, [loadBundle]);

  // Load narrative-flow after bundle (deferred, 50ms after bundle effect fires)
  useEffect(() => {
    if (!enabled || !isValidAddress(normalizedAddress)) return;
    const timer = setTimeout(() => {
      const run = async () => { try { await loadNarrativeFlow(); } catch (e) {} };
      run();
    }, 50);
    return () => clearTimeout(timer);
  }, [loadNarrativeFlow, enabled, normalizedAddress]);

  const anyLoading = Object.values(loading).some(Boolean);
  const anyError = Object.values(errors).find(Boolean) || '';

  return {
    data,
    loading,
    errors,
    anyLoading,
    anyError,
    lastUpdated,
    reload: () => { loadBundle({ force: true }); loadNarrativeFlow({ force: true }); },
    isValidAddress: isValidAddress(normalizedAddress),
  };
}
